import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, X, Sparkles } from "lucide-react";
import { BatchAutoMatchResults } from "@/hooks/useClients";

interface AutoMatchResultsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  results: BatchAutoMatchResults | null;
}

export function AutoMatchResultsDialog({ open, onOpenChange, results }: AutoMatchResultsDialogProps) {
  if (!results) return null;

  const matchedResults = results.matches.filter(m => m.matched);
  const unmatchedResults = results.matches.filter(m => !m.matched);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Auto-Match Results
          </DialogTitle>
          <DialogDescription>
            Matched {results.matched} of {results.total} clients to Dropbox folders.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* Summary badges */}
          <div className="flex gap-2 mb-4">
            <Badge variant="default" className="bg-green-600">
              <Check className="h-3 w-3 mr-1" />
              {results.matched} Matched
            </Badge>
            {results.unmatched > 0 && (
              <Badge variant="secondary">
                <X className="h-3 w-3 mr-1" />
                {results.unmatched} Unmatched
              </Badge>
            )}
          </div>

          {/* Results table */}
          <ScrollArea className="h-[300px] rounded-md border">
            <div className="divide-y">
              {/* Matched results first */}
              {matchedResults.map((result) => (
                <div
                  key={result.clientId}
                  className="flex items-center justify-between p-3 hover:bg-muted/50"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100 text-green-600 flex-shrink-0">
                      <Check className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{result.clientName}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        → {result.folderName}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Unmatched results */}
              {unmatchedResults.map((result) => (
                <div
                  key={result.clientId}
                  className="flex items-center justify-between p-3 hover:bg-muted/50"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-gray-400 flex-shrink-0">
                      <X className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{result.clientName}</p>
                      <p className="text-xs text-muted-foreground">
                        No matching folder found
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          {results.unmatched > 0 && (
            <p className="text-xs text-muted-foreground mt-3">
              Unmatched clients can be manually mapped by clicking the warning icon on their card.
            </p>
          )}

          <p className="text-xs text-muted-foreground mt-2">
            Note: Internal clients (e.g., "On Deck") are excluded from Dropbox folder mapping.
          </p>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
